import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiPlus, FiLogOut, FiHome } from 'react-icons/fi';
import { FaEye, FaEyeSlash, FaEdit, FaTrash } from 'react-icons/fa';

const AdminContainer = styled.div`
  min-height: 100vh;
  background: #f8fafc;
`;

const AdminHeader = styled.header`
  background: linear-gradient(135deg, var(--primary-blue) 0%, #1E40AF 100%);
  color: white;
  padding: 20px 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AdminTitle = styled.h1`
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0;
  color: white;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const HeaderButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const Section = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled.h2`
  color: var(--primary-blue);
  margin-bottom: 24px;
  font-size: 1.5rem;
  font-weight: 600;
`;

const AddEventButton = styled(motion.button)`
  background: var(--primary-orange);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
`;

const EventsGrid = styled.div`
  display: grid;
  gap: 20px;
  margin-top: 20px;
`;

const EventCard = styled(motion.div)`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  position: relative;
  transition: all 0.3s ease;
  ${props => props.hidden && `
    opacity: 0.7;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-left: 4px solid #dc2626;
  `}
  
  &:hover {
    border-color: var(--primary-orange);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const EventHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const EventTitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const EventTitle = styled.h3`
  color: var(--primary-blue);
  margin: 0;
  font-size: 1.2rem;
`;

const EventActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const ActionButton = styled.button`
  background: none;
  border: 1px solid #d1d5db;
  padding: 6px;
  border-radius: 4px;
  cursor: pointer;
  color: var(--medium-gray);
  transition: all 0.3s ease;

  &:hover {
    background: #f3f4f6;
  }

  &.danger {
    color: #dc2626;
    border-color: #fecaca;
    
    &:hover {
      background: #fef2f2;
    }
  }

  &.primary {
    color: var(--primary-orange);
    border-color: var(--primary-orange);
    
    &:hover {
      background: #fff7ed;
    }
  }
`;

const EventDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  font-size: 0.9rem;
  color: var(--medium-gray);
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  ${props => props.hidden ? `
    background: #fee2e2;
    color: #dc2626;
  ` : `
    background: #d1fae5;
    color: #059669;
  `}
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 30px;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  color: var(--primary-blue);
  margin-bottom: 24px;
  font-size: 1.5rem;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 20px;
  margin-bottom: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  color: var(--primary-blue);
  font-weight: 600;
  margin-bottom: 6px;
`;

const Input = styled.input`
  padding: 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary-orange);
  }
`;

const TextArea = styled.textarea`
  padding: 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  min-height: 80px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--primary-orange);
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &.primary {
    background: var(--primary-orange);
    color: white;
    border: none;
    
    &:hover {
      background: #ea580c;
    }
  }
  
  &.secondary {
    background: #f3f4f6;
    color: var(--medium-gray);
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #e5e7eb;
    }
  }
`;

const PhotoSection = styled.div`
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  background: #f9fafb;
`;

const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  margin: 16px 0;
`;

const PhotoItem = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #e5e7eb;
  background: white;
`;

const PhotoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PhotoRemove = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  
  &:hover {
    background: #b91c1c;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const PhotoUploadButton = styled.label`
  display: inline-block;
  background: var(--primary-orange);
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.3s ease;
  
  &:hover {
    background: #ea580c;
  }
  
  &:disabled {
    background: #d1d5db;
    cursor: not-allowed;
  }
`;

const PhotoInfo = styled.p`
  font-size: 0.9rem;
  color: var(--medium-gray);
  margin-top: 8px;
`;

const EventPhotoGallery = styled.div`
  margin-top: 12px;
`;

const EventPhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(60px, 1fr));
  gap: 8px;
  max-width: 300px;
`;

const EventPhotoThumb = styled.img`
  width: 100%;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
`;

const TabButton = styled.button`
  padding: 10px 24px;
  border: none;
  border-radius: 8px 8px 0 0;
  background: ${({ active }) => (active ? 'var(--primary-orange)' : '#f3f4f6')};
  color: ${({ active }) => (active ? 'white' : 'var(--primary-blue)')};
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  box-shadow: ${({ active }) => (active ? '0 -2px 8px rgba(0,0,0,0.06)' : 'none')};
`;

const RestoreButton = styled.button`
  background: #f3f4f6;
  color: var(--primary-orange);
  border: 1px solid var(--primary-orange);
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  margin-bottom: 16px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: var(--primary-orange);
    color: white;
  }
`;

const AdminDashboard = ({ onLogout }) => {
  const [events, setEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    location: '',
    sponsor: '',
    beneficiary: '',
    volunteers: '',
    burritos: '',
    served: '',
    description: '',
    photos: []
  });
  const [activeTab, setActiveTab] = useState('visible');

  // Configurable photo limit
  const MAX_PHOTOS_PER_EVENT = 5;

  // Load events from localStorage on component mount
  useEffect(() => {
    const savedEvents = localStorage.getItem('ahaarsewa_events');
    if (savedEvents) {
      setEvents(JSON.parse(savedEvents));
    } else {
      // Initialize with default events if none exist
      const defaultEvents = [
        {
          id: 1,
          title: "Holiday Burrito Drive",
          date: "December 15, 2024",
          location: "Sewa Community Center, Milpitas",
          sponsor: "TechForward Inc.",
          beneficiary: "Sacred Heart Community Service",
          volunteers: 25,
          burritos: 350,
          served: 350,
          description: "A heartwarming holiday event bringing together families and tech professionals to prepare nutritious meals for our community partners.",
          visible: true,
          photos: [
            {
              id: 1,
              name: "Sample Photo 1",
              data: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY2NzM4NSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkV2ZW50IFBob3RvPC90ZXh0Pjwvc3ZnPg=="
            },
            {
              id: 2,
              name: "Sample Photo 2", 
              data: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZWZmNmZmIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OGJhNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkNvbW11bml0eSBTZXJ2aWNlPC90ZXh0Pjwvc3ZnPg=="
            }
          ]
        },
        {
          id: 2,
          title: "Diwali Celebration Sewa",
          date: "November 8, 2024",
          location: "Hindu Community Center, Fremont",
          sponsor: "Patel Family Foundation",
          beneficiary: "Alameda County Food Bank",
          volunteers: 30,
          burritos: 400,
          served: 400,
          description: "Celebrating the festival of lights by spreading joy through service, sponsored in memory of beloved grandmother Sushila Patel.",
          visible: true,
          photos: [
            {
              id: 3,
              name: "Sample Photo 3",
              data: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZmVmM2NkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iI2Q5N2Y2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkRpd2FsaSBDZWxlYnJhdGlvbjwvdGV4dD48L3N2Zz4="
            }
          ]
        },
        {
          id: 3,
          title: "Team Building Burrito Event",
          date: "October 22, 2024",
          location: "Sewa Community Center, Milpitas",
          sponsor: "StartupSuccess Solutions",
          beneficiary: "Bill Wilson Center",
          volunteers: 20,
          burritos: 250,
          served: 250,
          description: "A corporate team building event that combined professional development with community service for a meaningful impact.",
          visible: true,
          photos: [
            {
              id: 4,
              name: "Sample Photo 4",
              data: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGNmZGY0Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzE2N2Y0NCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkNvcnBvcmF0ZSBTZXJ2aWNlPC90ZXh0Pjwvc3ZnPg=="
            },
            {
              id: 5,
              name: "Sample Photo 5",
              data: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZTBmMmZlIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzJkNjNkYiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPlRlYW0gQnVpbGRpbmc8L3RleHQ+PC9zdmc+"
            }
          ]
        },
        {
          id: 4,
          title: "Birthday Blessing Burritos",
          date: "September 18, 2024",
          location: "Community Kitchen, San Jose",
          sponsor: "The Kumar Family",
          beneficiary: "HomeFirst Services",
          volunteers: 15,
          burritos: 200,
          served: 200,
          description: "Celebrating young Arya Kumar's 10th birthday by serving others, teaching the next generation about compassion and giving.",
          visible: true,
          photos: [
            {
              id: 6,
              name: "Sample Photo 6",
              data: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZmVlMmU4Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iI2RjMjYyNiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkJpcnRoZGF5IFNlcnZpY2U8L3RleHQ+PC9zdmc+"
            }
          ]
        }
      ];
      setEvents(defaultEvents);
      localStorage.setItem('ahaarsewa_events', JSON.stringify(defaultEvents));
    }
  }, []);

  const saveEvents = (updatedEvents) => {
    setEvents(updatedEvents);
    localStorage.setItem('ahaarsewa_events', JSON.stringify(updatedEvents));
  };

  const handleAddEvent = () => {
    setEditingEvent(null);
    setFormData({
      title: '',
      date: '',
      location: '',
      sponsor: '',
      beneficiary: '',
      volunteers: '',
      burritos: '',
      served: '',
      description: '',
      photos: []
    });
    setShowModal(true);
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setFormData(event);
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (editingEvent) {
      // Update existing event
      const updatedEvents = events.map(event => 
        event.id === editingEvent.id 
          ? { ...formData, id: editingEvent.id, visible: editingEvent.visible }
          : event
      );
      saveEvents(updatedEvents);
    } else {
      // Add new event
      const newEvent = {
        ...formData,
        id: Date.now(),
        visible: true,
        isDefault: false,
        volunteers: parseInt(formData.volunteers),
        burritos: parseInt(formData.burritos),
        served: parseInt(formData.served)
      };
      saveEvents([...events, newEvent]);
    }
    
    setShowModal(false);
  };

  const toggleEventVisibility = (eventId) => {
    const updatedEvents = events.map(event =>
      event.id === eventId
        ? { ...event, visible: !event.visible }
        : event
    );
    saveEvents(updatedEvents);
  };

  const deleteEvent = (eventId) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      const updatedEvents = events.filter(event => event.id !== eventId);
      saveEvents(updatedEvents);
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const currentPhotos = formData.photos || [];
    
    if (currentPhotos.length + files.length > MAX_PHOTOS_PER_EVENT) {
      alert(`You can only upload up to ${MAX_PHOTOS_PER_EVENT} photos per event.`);
      return;
    }

    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const newPhoto = {
            id: Date.now() + Math.random(),
            name: file.name,
            data: event.target.result
          };
          setFormData(prev => ({
            ...prev,
            photos: [...(prev.photos || []), newPhoto]
          }));
        };
        reader.readAsDataURL(file);
      }
    });
    
    // Reset the input
    e.target.value = '';
  };

  const removePhoto = (photoId) => {
    setFormData(prev => ({
      ...prev,
      photos: (prev.photos || []).filter(photo => photo.id !== photoId)
    }));
  };

  const handleLogout = () => {
    localStorage.removeItem('ahaarsewa_admin_auth');
    onLogout();
  };

  const clearCacheAndReload = () => {
    // Only works on localhost for security
    if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      return;
    }
    
    if (window.confirm('This will clear all cached data and reload the page. You will need to log in again. Continue?')) {
      // Clear all localStorage
      localStorage.clear();
      // Clear all sessionStorage
      sessionStorage.clear();
      // Reload the page
      window.location.reload();
    }
  };

  const restoreDefaultEvents = () => {
    // Only works on localhost for security
    if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      return;
    }
      const defaultEvents = [
        {
          id: 1,
          title: "Holiday Burrito Drive",
          date: "December 15, 2024",
          location: "Sewa Community Center, Milpitas",
          sponsor: "TechForward Inc.",
          beneficiary: "Sacred Heart Community Service",
          volunteers: 25,
          burritos: 350,
          served: 350,
          description: "A heartwarming holiday event bringing together families and tech professionals to prepare nutritious meals for our community partners.",
          visible: true,
          isDefault: true,
          photos: [
            {
              id: 1,
              name: "Sample Photo 1",
              data: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY2NzM4NSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkV2ZW50IFBob3RvPC90ZXh0Pjwvc3ZnPg=="
            },
            {
              id: 2,
              name: "Sample Photo 2", 
              data: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZWZmNmZmIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OGJhNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkNvbW11bml0eSBTZXJ2aWNlPC90ZXh0Pjwvc3ZnPg=="
            }
          ]
        },
        {
          id: 2,
          title: "Diwali Celebration Sewa",
          date: "November 8, 2024",
          location: "Hindu Community Center, Fremont",
          sponsor: "Patel Family Foundation",
          beneficiary: "Alameda County Food Bank",
          volunteers: 30,
          burritos: 400,
          served: 400,
          description: "Celebrating the festival of lights by spreading joy through service, sponsored in memory of beloved grandmother Sushila Patel.",
          visible: true,
          isDefault: true,
          photos: [
            {
              id: 3,
              name: "Sample Photo 3",
              data: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZmVmM2NkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iI2Q5N2Y2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkRpd2FsaSBDZWxlYnJhdGlvbjwvdGV4dD48L3N2Zz4="
            }
          ]
        },
        {
          id: 3,
          title: "Team Building Burrito Event",
          date: "October 22, 2024",
          location: "Sewa Community Center, Milpitas",
          sponsor: "StartupSuccess Solutions",
          beneficiary: "Bill Wilson Center",
          volunteers: 20,
          burritos: 250,
          served: 250,
          description: "A corporate team building event that combined professional development with community service for a meaningful impact.",
          visible: true,
          isDefault: true,
          photos: [
            {
              id: 4,
              name: "Sample Photo 4",
              data: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGNmZGY0Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzE2N2Y0NCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkNvcnBvcmF0ZSBTZXJ2aWNlPC90ZXh0Pjwvc3ZnPg=="
            },
            {
              id: 5,
              name: "Sample Photo 5",
              data: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZTBmMmZlIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzJkNjNkYiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPlRlYW0gQnVpbGRpbmc8L3RleHQ+PC9zdmc+"
            }
          ]
        },
        {
          id: 4,
          title: "Birthday Blessing Burritos",
          date: "September 18, 2024",
          location: "Community Kitchen, San Jose",
          sponsor: "The Kumar Family",
          beneficiary: "HomeFirst Services",
          volunteers: 15,
          burritos: 200,
          served: 200,
          description: "Celebrating young Arya Kumar's 10th birthday by serving others, teaching the next generation about compassion and giving.",
          visible: true,
          isDefault: true,
          photos: [
            {
              id: 6,
              name: "Sample Photo 6",
              data: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZmVlMmU4Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iI2RjMjYyNiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkJpcnRoZGF5IFNlcnZpY2U8L3RleHQ+PC9zdmc+"
            }
          ]
        }
      ];
      
      // Keep all custom events (events that don't have isDefault=true or have IDs other than 1-4)
      const customEvents = events.filter(event => 
        !event.isDefault && ![1, 2, 3, 4].includes(event.id)
      );
      
      // Combine custom events with the restored default events
      const updatedEvents = [...customEvents, ...defaultEvents];
      
      console.log('Restoring defaults. Custom events preserved:', customEvents.length);
      console.log('Total events after restore:', updatedEvents.length);
      
      setEvents(updatedEvents);
      localStorage.setItem('ahaarsewa_events', JSON.stringify(updatedEvents));
  };

  // Filter events based on activeTab
  const filteredEvents = activeTab === 'visible'
    ? events.filter(event => event.visible)
    : events;

  // Debug logging
  console.log('Active tab:', activeTab);
  console.log('All events:', events.length);
  console.log('Filtered events:', filteredEvents.length);
  console.log('Events with visible=false:', events.filter(e => !e.visible).length);

  return (
    <AdminContainer>
      <AdminHeader>
        <HeaderContent>
          <AdminTitle>Ahaar Sewa Admin</AdminTitle>
          <HeaderActions>
            <HeaderButton onClick={() => window.open('/', '_blank')}>
              <FiHome /> View Site
            </HeaderButton>
            <HeaderButton onClick={handleLogout}>
              <FiLogOut /> Logout
            </HeaderButton>
          </HeaderActions>
        </HeaderContent>
      </AdminHeader>

      <Container>
        <Section>
          <SectionTitle>Event Management</SectionTitle>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
            <RestoreButton onClick={restoreDefaultEvents}>
              Restore Default Events
            </RestoreButton>
            <RestoreButton onClick={clearCacheAndReload}>
              Clear Cache & Reload
            </RestoreButton>
          </div>
          <AddEventButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleAddEvent}
          >
            <FiPlus /> Add New Event
          </AddEventButton>
          <TabsContainer>
            <TabButton
              active={activeTab === 'visible'}
              onClick={() => setActiveTab('visible')}
            >
              Visible Only
            </TabButton>
            <TabButton
              active={activeTab === 'all'}
              onClick={() => setActiveTab('all')}
            >
              All Events
            </TabButton>
          </TabsContainer>
          <EventsGrid>
            {filteredEvents.length === 0 ? (
              <div style={{ gridColumn: '1/-1', textAlign: 'center', color: 'var(--medium-gray)' }}>
                No events to display.
              </div>
            ) : (
              filteredEvents.map((event) => (
                <EventCard 
                  key={event.id} 
                  hidden={false}
                  style={{
                    opacity: event.visible ? 1 : 0.6,
                    background: event.visible ? 'white' : '#f8fafc',
                    border: event.visible ? '2px solid #d1d5db' : '2px solid #9ca3af',
                    borderRadius: '8px'
                  }}
                >
                  <EventHeader>
                    <EventTitleSection>
                      <EventTitle>{event.title}</EventTitle>
                      <StatusBadge hidden={!event.visible}>
                        {event.visible ? 'Visible' : 'Hidden'}
                      </StatusBadge>
                    </EventTitleSection>
                    <EventActions>
                      <ActionButton 
                        className="primary"
                        onClick={() => toggleEventVisibility(event.id)}
                      >
                        {event.visible ? <FaEye /> : <FaEyeSlash />}
                      </ActionButton>
                      <ActionButton onClick={() => handleEditEvent(event)}>
                        <FaEdit />
                      </ActionButton>
                      <ActionButton 
                        className="danger"
                        onClick={() => deleteEvent(event.id)}
                      >
                        <FaTrash />
                      </ActionButton>
                    </EventActions>
                  </EventHeader>
                  <EventDetails>
                    <div><strong>Date:</strong> {event.date}</div>
                    <div><strong>Location:</strong> {event.location}</div>
                    <div><strong>Sponsor:</strong> {event.sponsor}</div>
                    <div><strong>Beneficiary:</strong> {event.beneficiary}</div>
                    <div><strong>Volunteers:</strong> {event.volunteers}</div>
                    <div><strong>Burritos Made:</strong> {event.burritos}</div>
                    <div><strong>People Served:</strong> {event.served}</div>
                  </EventDetails>
                  {event.photos && event.photos.length > 0 && (
                    <EventPhotoGallery>
                      <EventPhotoGrid>
                        {event.photos.slice(0, 4).map((photo) => (
                          <EventPhotoThumb
                            key={photo.id}
                            src={photo.data}
                            alt={photo.name}
                          />
                        ))}
                        {event.photos.length > 4 && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#f3f4f6',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            color: 'var(--medium-gray)'
                          }}>
                            +{event.photos.length - 4}
                          </div>
                        )}
                      </EventPhotoGrid>
                    </EventPhotoGallery>
                  )}
                </EventCard>
              ))
            )}
          </EventsGrid>
        </Section>

        <Section>
          <SectionTitle>📧 Email Configuration Guide</SectionTitle>
          <div style={{ 
            background: '#f8fafc', 
            padding: '20px', 
            borderRadius: '8px', 
            border: '1px solid #e2e8f0',
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            lineHeight: '1.6'
          }}>
            <div style={{ marginBottom: '16px' }}>
              <strong style={{ color: 'var(--primary-blue)' }}>📍 Configuration File:</strong> <code>src/config/contact.js</code>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <strong style={{ color: 'var(--primary-blue)' }}>🎯 How to Use Multiple Emails:</strong>
            </div>
            
            <div style={{ marginLeft: '16px', marginBottom: '12px' }}>
              <div style={{ color: '#059669', marginBottom: '4px' }}>
                <strong>1. Get specific email type:</strong>
              </div>
                             <div style={{ marginLeft: '20px', color: '#374151' }}>
                 <code>getContactEmail('primary')</code> → burritosewa@gmail.com<br/>
                 <code>getContactEmail('support')</code> → samarth@honnu.com<br/>
                 <code>getContactEmail('admin')</code> → samarth@honnu.com
               </div>
            </div>
            
            <div style={{ marginLeft: '16px', marginBottom: '12px' }}>
              <div style={{ color: '#059669', marginBottom: '4px' }}>
                <strong>2. Get all unique emails:</strong>
              </div>
                             <div style={{ marginLeft: '20px', color: '#374151' }}>
                 <code>getAllEmails()</code> → ['burritosewa@gmail.com', 'samarth@honnu.com']
               </div>
            </div>
            
            <div style={{ marginLeft: '16px', marginBottom: '16px' }}>
              <div style={{ color: '#059669', marginBottom: '4px' }}>
                <strong>3. Get formatted list for display:</strong>
              </div>
                             <div style={{ marginLeft: '20px', color: '#374151' }}>
                 <code>getFormattedEmailList()</code> → 'burritosewa@gmail.com, samarth@honnu.com'
               </div>
            </div>
            
            <div style={{ 
              background: '#fef3c7', 
              padding: '12px', 
              borderRadius: '6px', 
              border: '1px solid #fbbf24',
              marginTop: '16px'
            }}>
              <strong style={{ color: '#92400e' }}>💡 Pro Tip:</strong> To add different emails (sponsor@, volunteer@, etc.), 
              edit the <code>emails</code> object in <code>contact.js</code> and they'll automatically work across the entire website!
            </div>
          </div>
        </Section>

        <Section>
          <SectionTitle>🚀 Future Ideas & Possibilities</SectionTitle>
          <div style={{ 
            background: '#f0f9ff', 
            padding: '20px', 
            borderRadius: '8px', 
            border: '1px solid #bae6fd',
            fontSize: '0.95rem',
            lineHeight: '1.6'
          }}>
            <div style={{ marginBottom: '20px' }}>
              <strong style={{ color: 'var(--primary-blue)', fontSize: '1.1rem' }}>📋 Ideas to Consider Later:</strong>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              <div style={{ 
                background: 'white', 
                padding: '16px', 
                borderRadius: '6px', 
                border: '1px solid #e0f2fe' 
              }}>
                <div style={{ color: '#1565c0', fontWeight: 'bold', marginBottom: '8px' }}>
                  👥 Corporate Team Building
                </div>
                <div style={{ color: '#374151', fontSize: '0.9rem' }}>
                  • Allow sponsors to bring employees for volunteering<br/>
                  • Team building activities during burrito making<br/>
                  • Coordinate with volunteer pool for training/management<br/>
                  • Premium pricing tier for this service
                </div>
              </div>
              
              <div style={{ 
                background: 'white', 
                padding: '16px', 
                borderRadius: '6px', 
                border: '1px solid #e0f2fe' 
              }}>
                <div style={{ color: '#1565c0', fontWeight: 'bold', marginBottom: '8px' }}>
                  📞 Dedicated Phone Number
                </div>
                <div style={{ color: '#374151', fontSize: '0.9rem' }}>
                  • Get a dedicated phone line for Ahaar Sewa<br/>
                  • Google Voice or business line option<br/>
                  • Set up voicemail with professional greeting<br/>
                  • Consider business hours for responses
                </div>
              </div>
              
              <div style={{ 
                background: 'white', 
                padding: '16px', 
                borderRadius: '6px', 
                border: '1px solid #e0f2fe' 
              }}>
                <div style={{ color: '#1565c0', fontWeight: 'bold', marginBottom: '8px' }}>
                  🎯 Enhanced Sponsorship
                </div>
                <div style={{ color: '#374151', fontSize: '0.9rem' }}>
                  • Monthly recurring sponsors<br/>
                  • Corporate partnership tiers<br/>
                  • Sponsor recognition programs<br/>
                  • Annual sponsor appreciation events
                </div>
              </div>
              
              <div style={{ 
                background: 'white', 
                padding: '16px', 
                borderRadius: '6px', 
                border: '1px solid #e0f2fe' 
              }}>
                <div style={{ color: '#1565c0', fontWeight: 'bold', marginBottom: '8px' }}>
                  📈 Impact Tracking
                </div>
                <div style={{ color: '#374151', fontSize: '0.9rem' }}>
                  • Annual impact reports for sponsors<br/>
                  • Photo galleries from each event<br/>
                  • Community partner feedback<br/>
                  • Social media highlight reels
                </div>
              </div>
            </div>
            
            <div style={{ 
              marginTop: '20px', 
              padding: '12px', 
              background: '#fef3c7', 
              borderRadius: '6px', 
              border: '1px solid #fbbf24' 
            }}>
              <strong style={{ color: '#92400e' }}>💡 Note:</strong> These are ideas for future expansion. 
              Current focus is on streamlined sponsorship and event execution with our volunteer pool.
            </div>
          </div>
        </Section>
      </Container>

      {showModal && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
          >
            <ModalTitle>
              {editingEvent ? 'Edit Event' : 'Add New Event'}
            </ModalTitle>
            <form onSubmit={handleSubmit}>
              <FormGrid>
                <FormGroup>
                  <Label>Event Title</Label>
                  <Input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Date</Label>
                  <Input
                    type="text"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    placeholder="e.g., December 15, 2024"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Location</Label>
                  <Input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Sponsor</Label>
                  <Input
                    type="text"
                    value={formData.sponsor}
                    onChange={(e) => setFormData({...formData, sponsor: e.target.value})}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Beneficiary Organization</Label>
                  <Input
                    type="text"
                    value={formData.beneficiary}
                    onChange={(e) => setFormData({...formData, beneficiary: e.target.value})}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Number of Volunteers</Label>
                  <Input
                    type="number"
                    value={formData.volunteers}
                    onChange={(e) => setFormData({...formData, volunteers: e.target.value})}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Burritos Made</Label>
                  <Input
                    type="number"
                    value={formData.burritos}
                    onChange={(e) => setFormData({...formData, burritos: e.target.value})}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>People Served</Label>
                  <Input
                    type="number"
                    value={formData.served}
                    onChange={(e) => setFormData({...formData, served: e.target.value})}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Description</Label>
                  <TextArea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Event Photos ({(formData.photos || []).length}/{MAX_PHOTOS_PER_EVENT})</Label>
                  <PhotoSection>
                    <PhotoUploadButton htmlFor="photo-upload">
                      <FiPlus style={{marginRight: '8px'}} />
                      Upload Photos
                    </PhotoUploadButton>
                    <FileInput
                      id="photo-upload"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoUpload}
                      disabled={(formData.photos || []).length >= MAX_PHOTOS_PER_EVENT}
                    />
                    <PhotoInfo>
                      Upload up to {MAX_PHOTOS_PER_EVENT} photos. Supported formats: JPG, PNG, GIF
                    </PhotoInfo>
                    {formData.photos && formData.photos.length > 0 && (
                      <PhotoGrid>
                        {formData.photos.map((photo) => (
                          <PhotoItem key={photo.id}>
                            <PhotoImage src={photo.data} alt={photo.name} />
                            <PhotoRemove onClick={() => removePhoto(photo.id)}>
                              ×
                            </PhotoRemove>
                          </PhotoItem>
                        ))}
                      </PhotoGrid>
                    )}
                  </PhotoSection>
                </FormGroup>
              </FormGrid>
              <ModalActions>
                <Button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="primary">
                  {editingEvent ? 'Update Event' : 'Add Event'}
                </Button>
              </ModalActions>
            </form>
          </ModalContent>
        </Modal>
      )}
    </AdminContainer>
  );
};

export default AdminDashboard; 